/**
 * 제안서 — 공고 → 검토 → 재무계획을 지난 프로젝트가 쓴다. **2026-09-29 부터 실제 DB** (`consult_proposals`).
 *
 * **룰베이스가 목차와 칸을 만들고, 사람과 AI 가 채운다** (#5). 양식은 DB 시드 셋 —
 * 공공 입찰(문서) · 민간 컨설팅(슬라이드, `GRK_제안서_양식.pptx` 순서) · 공통 기본(6칸).
 * 실제 제안서 16건의 구조가 근거다(`docs/02-design/features/proposal-structure.md`). 양식은 출발점 —
 * 사람이 칸을 더하고 · 빼고 · 이름을 바꾼다.
 *
 *   룰    — 이 프로젝트에 이미 있는 것에서 그대로 가져온다 (공고 · 재무계획 · 아카이브 · 배점표)
 *   AI    — 제안요청서 · 앞 칸 초안을 읽고 초안을 쓴다. **사람이 승인하기 전까지는 초안이다**
 *   직접  — 사람이 쓴다. 고정문(회사 소개) 뼈대가 깔리고, 규칙이 있으면 자료로 미리 채운다
 *   절    — 부 머리. 본문 없음
 *
 * 슬라이드 양식이면 칸 제목이 **한 문장 주장**(슬라이드 제목)이고 본문이 그 근거다.
 * 백엔드 `modules/proposal/proposal.service.ts` 의 View 와 같다.
 */
export const PROPOSAL_STATUSES = ['작성 중', '검토 중', '승인', '제출'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export const PROPOSAL_STATUS_STYLE: Record<ProposalStatus, string> = {
  '작성 중': 'bg-slate-100 text-slate-600',
  '검토 중': 'bg-amber-50 text-amber-700',
  '승인': 'bg-emerald-50 text-emerald-700',
  '제출': 'bg-indigo-50 text-indigo-600',
};

export const FILL_KINDS = ['룰', 'AI', '직접', '절'] as const;
export type FillKind = (typeof FILL_KINDS)[number];

export const SLOT_STATES = ['비어 있음', '초안', '채움'] as const;
export type SlotState = (typeof SLOT_STATES)[number];

export const SLOT_STATE_STYLE: Record<SlotState, string> = {
  '비어 있음': 'bg-slate-100 text-slate-500',
  '초안': 'bg-amber-50 text-amber-700',
  '채움': 'bg-emerald-50 text-emerald-700',
};

export const SOURCE_KINDS = ['공고', '검토 메모', '재무계획', '아카이브', '제안요청서', '배점표', '직접'] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

export const SOURCE_STYLE: Record<SourceKind, string> = {
  '공고': 'bg-indigo-50 text-indigo-600',
  '검토 메모': 'bg-slate-100 text-slate-600',
  '재무계획': 'bg-emerald-50 text-emerald-700',
  '아카이브': 'bg-amber-50 text-amber-700',
  '제안요청서': 'bg-sky-50 text-sky-700',
  '배점표': 'bg-violet-50 text-violet-700',
  '직접': 'bg-slate-100 text-slate-500',
};

export type TemplateFormat = '슬라이드' | '문서';

export interface SourceRef {
  id: number;
  kind: SourceKind;
  label: string;
  documentId: number | null;
  rfpFileId: number | null;
  pageNo: number | null;
}

export interface ProposalSection {
  id: number;
  /** 목차 코드 — I · II-1 · A-1 */
  code: string;
  parentCode: string | null;
  sortOrder: number;
  /** 슬라이드 양식이면 한 문장 주장 */
  title: string;
  fill: FillKind;
  state: SlotState;
  /** 겨냥하는 평가항목(힌트) */
  evalCategory: string | null;
  ruleKey: string | null;
  aiGuide: string | null;
  /** 먼저 채워져야 하는 칸 — 이 제안서의 칸 코드로 풀어 준 것 */
  dependsOn: string[];
  isRequired: boolean;
  /** 사람이 더한 칸(지울 수 있다) */
  custom: boolean;
  /** 더한 칸이 대응하는 공통 칸(BASIC 1~6) 코드 — 그 칸의 지침 · 룰 · 뼈대를 받는다. 양식 칸이면 null */
  basisCode: string | null;
  /** 이 칸이 다루는 배점 항목 — 공공 첫 장 「평가항목 조견표」의 「해당 칸」 */
  evalItemIds: number[];
  content: string;
  aiProvider: string | null;
  aiModel: string | null;
  aiAnalysis: string | null;
  aiDraftedAt: string | null;
  editedAt: string | null;
  approvedAt: string | null;
  sources: SourceRef[];
}

export interface Proposal {
  id: number;
  projectId: number;
  templateCode: string;
  templateName: string;
  format: TemplateFormat;
  version: number;
  status: ProposalStatus;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sections: ProposalSection[];
  filled: number;
  slots: number;
}

export interface ProposalTemplate {
  code: string;
  name: string;
  bizKind: string;
  format: TemplateFormat;
  description: string | null;
  slots: number;
}

/** 기관 · RFP 목차를 붙여 넣은 칸이 고르는 공통 칸(BASIC 1~6) */
export interface BasisSlot { code: string; title: string; fill: FillKind; ruleKey: string | null }

export interface ProposalSummary {
  proposal: Proposal | null;
  suggestedTemplate: string;
  templates: ProposalTemplate[];
  basisSlots: BasisSlot[];
}

export interface DraftAllStatus {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  total: number;
  done: number;
  current: string | null;
  results: { code: string; title: string; ok: boolean; note?: string }[];
}

/** 부 → 그 아래 칸. 부 없는 칸은 앞뒤 순서 그대로 한 묶음씩 */
export interface TocPart { part: ProposalSection | null; slots: ProposalSection[] }

export function toc(sections: ProposalSection[]): TocPart[] {
  const out: TocPart[] = [];
  const parts = sections.filter((s) => s.fill === '절');
  for (const s of sections) {
    if (s.fill === '절') { out.push({ part: s, slots: sections.filter((x) => x.parentCode === s.code && x.fill !== '절') }); continue; }
    if (s.parentCode && parts.some((p) => p.code === s.parentCode)) continue;
    const last = out[out.length - 1];
    if (last && last.part === null) last.slots.push(s);
    else out.push({ part: null, slots: [s] });
  }
  return out;
}
